import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FileType2 } from "lucide-react";
import { CloudUploadIcon } from "../../components/icons";

import CfLoader from "../CfLoader/CfLoader";
import getClickableProps from "../../hooks/getClickableProps";

const MAX_FILE_SIZE = 25 * 1024 * 1024;

function CertificateAssetUploadSlot({
  id,
  stepNumber,
  label,
  helperText,
  formatHint,
  acceptedExtensions,
  previewType,
  uploadedAsset,
  isUploading,
  onFileSelect,
  onRemove,
  disabled = false,
  required = false,
  t,
}) {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [stylesheetPreviewReady, setStylesheetPreviewReady] = useState(false);
  const [stylesheetPreviewFailed, setStylesheetPreviewFailed] = useState(false);
  const stylesheetLinkId = `cert-onboarding-stylesheet-${id}`;

  useEffect(() => {
    if (previewType !== "stylesheet" || !uploadedAsset?.url) {
      setStylesheetPreviewReady(false);
      setStylesheetPreviewFailed(false);
      return undefined;
    }

    setStylesheetPreviewReady(false);
    setStylesheetPreviewFailed(false);

    const link = document.createElement("link");
    link.id = stylesheetLinkId;
    link.rel = "stylesheet";
    link.href = uploadedAsset.url;
    let cancelled = false;

    const handleLoad = () => {
      if (!cancelled) setStylesheetPreviewReady(true);
    };
    const handleError = () => {
      if (!cancelled) setStylesheetPreviewFailed(true);
    };

    link.addEventListener("load", handleLoad);
    link.addEventListener("error", handleError);
    document.head.appendChild(link);

    return () => {
      cancelled = true;
      link.removeEventListener("load", handleLoad);
      link.removeEventListener("error", handleError);
      document.getElementById(stylesheetLinkId)?.remove();
    };
  }, [previewType, uploadedAsset?.url, stylesheetLinkId]);

  const validateAndSelect = (file, inputElement) => {
    if (!file) return;

    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    const isValidType = acceptedExtensions.includes(extension);

    if (!isValidType) {
      if (inputElement) {
        inputElement.value = "";
      }
      onFileSelect(null, "invalid_type");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      if (inputElement) {
        inputElement.value = "";
      }
      onFileSelect(null, "size");
      return;
    }

    if (inputElement) {
      inputElement.value = "";
    }
    onFileSelect(file);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    validateAndSelect(file, event.target);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isUploading && !disabled) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (isUploading || disabled) return;
    const file = event.dataTransfer.files[0];
    validateAndSelect(file, fileInputRef.current);
  };

  const handleRemove = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onRemove();
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const acceptAttr = acceptedExtensions.map((ext) => `.${ext}`).join(",");
  const isUploaded = Boolean(uploadedAsset && !isUploading);
  const isInteractionLocked = isUploading || disabled;
  const helperId = `${id}-helper`;
  const uploadZoneLabel = `${label} ${t("certificateOnboarding:upload_click")}`;
  const cardBorderClass = isUploaded
    ? "border-cfCardBorder border-l-4 border-l-cfPrimary"
    : "border-cfCardBorder";

  return (
    <div
      className={`rounded-xl border bg-cfSecondary p-4 shadow-sm transition-all duration-200 ${cardBorderClass} ${disabled ? "opacity-70 pointer-events-none cursor-not-allowed" : ""}`}
    >
      <div className="mb-3 flex items-start gap-3">
        {stepNumber && (
          <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 rounded-md bg-cfPrimary text-cfSecondary text-xs font-semibold shrink-0">
            {stepNumber}
          </span>
        )}
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-cfHeadingText">
            {label}
            {required && (
              <span
                className="text-cfStatusBlockedText ml-2"
                aria-hidden="true"
              >
                *
              </span>
            )}
          </h3>
          <p id={helperId} className="text-xs text-cfGlobalText mt-0.5">
            {helperText}
          </p>
        </div>
      </div>

      {uploadedAsset && !isUploading ? (
        <div className="relative rounded-lg border border-cfCardBorder p-3 bg-cfSecondary">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-cfHeadingText truncate pr-2">
              {uploadedAsset.file_name}
            </span>
            <button
              type="button"
              id={`${id}-remove`}
              onClick={handleRemove}
              className="shrink-0 text-cfStatusBlockedText hover:opacity-80 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary rounded-sm"
            >
              {t("certificateOnboarding:remove")}
            </button>
          </div>

          {previewType === "svg" && uploadedAsset.url && (
            <div className="flex justify-center items-center bg-cfSecondary rounded-md p-2 min-h-[120px]">
              <img
                src={uploadedAsset.url}
                alt={label}
                className="max-w-full h-auto max-h-48 object-contain rounded-md"
              />
            </div>
          )}

          {previewType === "stylesheet" && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-cfGlobalText">
                <FileType2 className="w-5 h-5 text-cfPrimary shrink-0" />
                <span className="text-xs">{formatHint}</span>
              </div>
              {stylesheetPreviewReady && (
                <p className="text-sm text-cfHeadingText">
                  {t("certificateOnboarding:stylesheet_preview_ready")}
                </p>
              )}
              {stylesheetPreviewFailed && (
                <p className="text-xs text-cfGlobalText">
                  {t("certificateOnboarding:stylesheet_preview_failed")}
                </p>
              )}
            </div>
          )}
        </div>
      ) : (
        <div
          {...getClickableProps(openFilePicker, {
            disabled: isInteractionLocked,
            label: uploadZoneLabel,
          })}
          aria-describedby={helperText ? helperId : undefined}
          className={`flex gap-4 py-6 px-4 cursor-pointer text-cfGlobalText rounded-lg outline-dotted outline-cfCardBorder outline-offset-2 shadow-sm transition-colors duration-200 cf-focus-ring ${
            dragActive
              ? "bg-cfSecondary"
              : "bg-cfSecondary/50 hover:bg-cfSecondary"
          } ${isUploading ? "pointer-events-none opacity-70" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept={acceptAttr}
            onChange={handleFileChange}
            className="hidden"
            id={id}
            ref={fileInputRef}
            disabled={isInteractionLocked}
            tabIndex={-1}
            aria-hidden="true"
          />
          <div
            className={`flex flex-row w-full pointer-events-none ${isUploading ? "cursor-wait" : ""}`}
            aria-hidden="true"
          >
            {isUploading ? (
              <div className="flex items-center gap-3 w-full justify-center py-2">
                <div className="scale-50 origin-center">
                  <CfLoader />
                </div>
                <span className="text-sm text-cfGlobalText">
                  {t("certificateOnboarding:uploading")}
                </span>
              </div>
            ) : (
              <>
                <CloudUploadIcon
                  size={36}
                  className="shrink-0 my-auto mr-4 inline-flex text-cfPrimary"
                  aria-hidden
                />
                <div className="flex flex-col">
                  <div className="text-sm">
                    <span className="font-semibold text-cfPrimary">
                      {t("certificateOnboarding:upload_click")}{" "}
                    </span>
                    <span className="text-cfGlobalText">
                      {t("certificateOnboarding:upload_drag")}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-cfGlobalText">
                    {formatHint}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CertificateAssetUploadSlot;

CertificateAssetUploadSlot.propTypes = {
  id: PropTypes.string.isRequired,
  stepNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string.isRequired,
  helperText: PropTypes.string,
  formatHint: PropTypes.string,
  acceptedExtensions: PropTypes.arrayOf(PropTypes.string).isRequired,
  previewType: PropTypes.oneOf(["svg", "stylesheet"]).isRequired,
  uploadedAsset: PropTypes.shape({
    file_name: PropTypes.string,
    url: PropTypes.string,
  }),
  isUploading: PropTypes.bool,
  onFileSelect: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};
